/** Pure state → request mapping and client-side checks of the "new customer" form. */
import { isValidIranMobileNumber, toEnglishDigits } from '../../lib/phoneNumber.ts';

export type CustomerFormKind = 'person' | 'organization';
export type PhoneKind = 'landline' | 'mobile' | 'other';

export interface PhoneRow {
  readonly id: number;
  readonly kind: PhoneKind;
  readonly number: string;
  readonly label: string;
}

export interface CustomerFormState {
  readonly kind: CustomerFormKind;
  /** Customer code: pre-filled with the suggested one, editable. */
  readonly code: string;
  readonly name: string;
  readonly nationalId: string;
  readonly economicCode: string;
  readonly registrationNumber: string;
  readonly mobile: string;
  readonly phone: string;
  readonly phones: readonly PhoneRow[];
  readonly contactName: string;
  readonly contactTitle: string;
  readonly contactMobile: string;
  readonly province: string;
  readonly city: string;
  readonly postalCode: string;
  readonly address: string;
  readonly ownerMemberRef: string;
  /** Labels (ids from /crm/labels) picked or created in place. */
  readonly labelIds: readonly string[];
}

export const PHONE_KIND_LABEL: Readonly<Record<PhoneKind, string>> = {
  landline: 'تلفن ثابت',
  mobile: 'موبایل',
  other: 'سایر',
};

export const MAX_PHONE_ROWS = 10;

export function emptyCustomerForm(kind: CustomerFormKind = 'organization'): CustomerFormState {
  return {
    kind,
    code: '',
    name: '',
    nationalId: '',
    economicCode: '',
    registrationNumber: '',
    mobile: '',
    phone: '',
    phones: [],
    contactName: '',
    contactTitle: '',
    contactMobile: '',
    province: '',
    city: '',
    postalCode: '',
    address: '',
    ownerMemberRef: '',
    labelIds: [],
  };
}

/** Changing legal personality keeps the entire draft, including temporarily hidden fields. */
export function changeCustomerKind(
  form: CustomerFormState,
  kind: CustomerFormKind,
): CustomerFormState {
  return { ...form, kind };
}

const digits = (value: string): string => toEnglishDigits(value).replace(/[\s-]/g, '');

/** Field key → Persian message; empty object when the form can be submitted. */
export function validateCustomerForm(form: CustomerFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  const legal = form.kind === 'organization';
  if (form.name.trim() === '')
    errors['name'] = legal ? 'نام شرکت را وارد کنید.' : 'نام و نام خانوادگی را وارد کنید.';
  const nationalId = digits(form.nationalId);
  if (nationalId !== '') {
    const length = legal ? 11 : 10;
    if (!new RegExp(`^[0-9]{${length}}$`).test(nationalId))
      errors['nationalId'] = legal ? 'شناسه ملی باید ۱۱ رقم باشد.' : 'کد ملی باید ۱۰ رقم باشد.';
  }
  if (
    legal &&
    digits(form.economicCode) !== '' &&
    !/^[0-9]{11,12}$/.test(digits(form.economicCode))
  )
    errors['economicCode'] = 'شناسهٔ ملی ۱۱ رقمی یا کد اقتصادی قدیمی ۱۲ رقمی را وارد کنید.';
  if (
    legal &&
    digits(form.registrationNumber) !== '' &&
    !/^[0-9]{1,20}$/.test(digits(form.registrationNumber))
  )
    errors['registrationNumber'] = 'شماره ثبت فقط شامل رقم است.';
  if (!legal && form.mobile.trim() !== '' && !isValidIranMobileNumber(form.mobile))
    errors['mobile'] = 'شماره موبایل باید با ۰۹ شروع شود و ۱۱ رقم باشد.';
  if (!legal && digits(form.phone) !== '' && !/^[0-9]{3,20}$/.test(digits(form.phone)))
    errors['phone'] = 'تلفن ثابت نامعتبر است.';
  if (digits(form.postalCode) !== '' && !/^[0-9]{10}$/.test(digits(form.postalCode)))
    errors['postalCode'] = 'کد پستی باید ۱۰ رقم باشد.';
  if (form.city !== '' && form.province === '') errors['province'] = 'ابتدا استان را انتخاب کنید.';
  if (legal) {
    form.phones.forEach((row, index) => {
      if (row.number.trim() === '') return;
      if (!/^[0-9]{3,20}$/.test(digits(row.number)))
        errors[`extraPhones.${index}.number`] = 'شماره تلفن نامعتبر است.';
      else if (row.kind === 'mobile' && !isValidIranMobileNumber(row.number))
        errors[`extraPhones.${index}.number`] = 'موبایل باید با ۰۹ شروع شود و ۱۱ رقم باشد.';
    });
    const hasContact = form.contactTitle.trim() !== '' || digits(form.contactMobile) !== '';
    if (hasContact && form.contactName.trim() === '')
      errors['contactPerson.fullName'] = 'نام شخص در ارتباط را وارد کنید.';
    if (digits(form.contactMobile) !== '' && !isValidIranMobileNumber(form.contactMobile))
      errors['contactPerson.mobile'] = 'موبایل باید با ۰۹ شروع شود و ۱۱ رقم باشد.';
  }
  return errors;
}

/** Request body for `POST /crm/customers`; blank fields are omitted, never sent as "". */
export function buildCustomerPayload(form: CustomerFormState): Record<string, unknown> {
  const legal = form.kind === 'organization';
  const body: Record<string, unknown> = { kind: form.kind, name: form.name.trim() };
  if (form.code.trim() !== '') body['code'] = form.code.trim();
  const put = (key: string, value: string, normalize: (v: string) => string = (v) => v.trim()) => {
    const text = normalize(value);
    if (text !== '') body[key] = text;
  };
  put('nationalId', form.nationalId, digits);
  put('province', form.province);
  put('city', form.city);
  put('postalCode', form.postalCode, digits);
  put('address', form.address);
  put('ownerMemberRef', form.ownerMemberRef);
  if (form.labelIds.length > 0) body['labelIds'] = [...form.labelIds];
  if (legal) {
    put('economicCode', form.economicCode, digits);
    put('registrationNumber', form.registrationNumber, digits);
    const phones = form.phones
      .filter((row) => row.number.trim() !== '')
      .map((row) => ({
        kind: row.kind,
        number: digits(row.number),
        ...(row.label.trim() === '' ? {} : { label: row.label.trim() }),
      }));
    if (phones.length > 0) body['extraPhones'] = phones;
    if (form.contactName.trim() !== '') {
      body['contactPerson'] = {
        fullName: form.contactName.trim(),
        ...(form.contactTitle.trim() === '' ? {} : { jobTitle: form.contactTitle.trim() }),
        ...(digits(form.contactMobile) === '' ? {} : { mobile: digits(form.contactMobile) }),
      };
    }
  } else {
    put('mobile', form.mobile, digits);
    put('phone', form.phone, digits);
  }
  return body;
}
